import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/** The tone a Frontdoor status badge carries. */
export type FdBadgeTone = 'ok' | 'warn' | 'err' | 'muted';

/** The tone a dashboard draws in. Its pills, chips and foot lines name an error `crit`. */
export type FdDashboardTone = 'ok' | 'warn' | 'crit' | 'muted';

/** One readout in the strip under a dashboard's identity line. */
export interface FdReadout {
    key: string;
    label: string;
    value: string;
    /** Draws the value at body size, for a long name such as a domain, where the large figure would cut it short. */
    smallValue?: boolean;
    /** Spans the free columns of the strip, so a long value shows in full. Set it on the last readout only. */
    wide?: boolean;
    foot: string;
    /** More about the foot, as a tooltip, such as why a step failed. */
    footTitle?: string;
    /** The PrimeNG icon in the readout's chip. */
    icon: string;
    /** Colours the foot line where the reading carries a state of its own; 'none' leaves it quiet. */
    footTone: FdDashboardTone | 'none';
}

export function dashboardTone(tone: FdBadgeTone): FdDashboardTone {
    return tone === 'err' ? 'crit' : tone;
}

/** Warnings and failures colour a readout's foot line. Healthy and idle readings stay quiet. */
export function readoutFootTone(tone: FdBadgeTone): FdDashboardTone | 'none' {
    return tone === 'warn' || tone === 'err' ? dashboardTone(tone) : 'none';
}

/** How many items a dashboard's title switcher reads at once. Typing in it searches the server for the rest. */
export const FD_SWITCH_PAGE_SIZE = 100;

/**
 * A title switcher search: the first page of items whose name contains the term, by name. The service turns the term
 * into a contains-pattern. A blank term sends no name filter.
 */
export function fdNameSearch<P, T>(
    find: (sort: string, size: number, filters: { name?: string }) => Observable<P>,
    rows: (page: P) => T[]
): (term: string) => Observable<T[]> {
    return (term) => {
        const name = term.trim();
        return find('name,ASC', FD_SWITCH_PAGE_SIZE, name ? { name } : {}).pipe(map(rows));
    };
}
